import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, Bot, User, Lightbulb, Zap, X } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const LLMAssistPanel = ({ flow, onClose, onSuggestion }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: 'Hi! I\'m your AI Flow assistant. I can help you optimize your workflow, suggest improvements, debug issues, and provide guidance on best practices. What would you like help with?',
      timestamp: new Date(),
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const base = (API_BASE_URL || '/api').replace(/\/$/, '');

      const flowContext = flow ? `Flow has ${flow.nodes?.length || 0} nodes and ${flow.edges?.length || 0} edges. Node types: ${[...new Set(flow.nodes?.map(n => n.type) || [])].join(', ')}.` : '';
      const systemPrompt = `You are an AI Flow builder assistant. Help users build, optimize, and debug their AI workflows. ${flowContext} Give concise, actionable advice.`;

      // Build conversation history for context
      const history = messages
        .filter(m => m.type === 'user' || m.type === 'assistant')
        .slice(-6)
        .map(m => ({ role: m.type === 'user' ? 'user' : 'assistant', content: m.content }));

      const response = await fetch(`${base}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: inputMessage, systemPrompt, history })
      });

      if (!response.ok) {
        throw new Error(`AI request failed (${response.status})`);
      }

      const data = await response.json();
      const aiContent = data.reply || 'Sorry, I could not generate a response.';

      // Extract actionable suggestions from AI response keywords
      const suggestions = [];
      if (/optim|improv|enhanc/i.test(aiContent)) {
        suggestions.push({ type: 'add_node', nodeType: 'condition', label: 'Add Error Handling' });
      }
      if (/validat/i.test(aiContent)) {
        suggestions.push({ type: 'add_node', nodeType: 'dataTransform', label: 'Add Data Validation' });
      }
      if (/llm|ai|model/i.test(aiContent)) {
        suggestions.push({ type: 'add_node', nodeType: 'llm', label: 'Add LLM Node' });
      }

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: aiContent,
        suggestions: suggestions.length > 0 ? suggestions : undefined,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I'm having trouble connecting to the AI service. Please check that the Worker is deployed and try again.`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const applySuggestion = (suggestion) => {
    if (onSuggestion) {
      onSuggestion(suggestion);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickPrompts = [
    'How can I optimize this flow?',
    'Help me debug an error',
    'Suggest better prompts for my LLM nodes',
    'What\'s the best workflow structure?'
  ];

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <Bot size={16} className="text-purple-600" />
          </div>
          <h3 className="font-semibold text-gray-800">AI Assistant</h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div key={message.id} className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : ''}`}>
            {message.type === 'assistant' && (
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <Bot size={12} className="text-purple-600" />
              </div>
            )}
            
            <div className={`max-w-[80%] ${message.type === 'user' ? 'order-first' : ''}`}>
              <div className={`p-3 rounded-lg ${
                message.type === 'user' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                <div className="text-sm whitespace-pre-wrap">{message.content}</div>
              </div>
              
              {message.suggestions && (
                <div className="mt-2 space-y-1">
                  {message.suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => applySuggestion(suggestion)}
                      className="block w-full text-left p-2 text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 rounded border border-purple-200 transition-colors"
                    >
                      <Lightbulb size={10} className="inline mr-1" />
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              )}
              
              <div className="text-xs text-gray-500 mt-1">
                {message.timestamp.toLocaleTimeString()}
              </div>
            </div>
            
            {message.type === 'user' && (
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <User size={12} className="text-blue-600" />
              </div>
            )}
          </div>
        ))}
        
        {isLoading && (
          <div className="flex gap-3">
            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <Bot size={12} className="text-purple-600" />
            </div>
            <div className="bg-gray-100 p-3 rounded-lg">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Prompts */}
      {messages.length === 1 && (
        <div className="p-4 border-t border-gray-100">
          <div className="text-xs text-gray-500 mb-2">Quick prompts:</div>
          <div className="space-y-1">
            {quickPrompts.map((prompt, index) => (
              <button
                key={index}
                onClick={() => setInputMessage(prompt)}
                className="block w-full text-left p-2 text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 rounded transition-colors"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about your flow..."
            className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
            rows="2"
          />
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LLMAssistPanel;
